import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import {
  PlusIcon, MagnifyingGlassIcon, TrashIcon, CurrencyDollarIcon,
  UserIcon, ChevronDownIcon, ChevronUpIcon, CheckIcon, XMarkIcon, TruckIcon
} from '@heroicons/react/24/outline';
import { Button } from '../components/ui/button';
import { ButtonGroup } from '../components/ui/button-group';
import { Input } from '../components/ui/input';
import { DatePicker } from '../components/ui/date-picker';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tip } from '../components/ui/tip';
import { confirmDialog } from '../components/ConfirmDialog';
import { hoyLocal } from '../utils/fechaLocal';

const FREQUENCIES = [
  { value: 'daily',   label: 'Diario' },
  { value: 'weekly',  label: 'Semanal' },
  { value: 'biweekly',label: 'Quincenal' },
  { value: 'monthly', label: 'Mensual' },
  { value: 'custom',  label: 'Cada X días' },
];
const FREQ_LABEL: Record<string, string> = Object.fromEntries(FREQUENCIES.map(f => [f.value, f.label]));

const PAYMENT_TYPES = [
  { value: 'sueldo',    label: 'Sueldo' },
  { value: 'anticipo',  label: 'Anticipo' },
  { value: 'comision',  label: 'Comisión' },
  { value: 'bonus',     label: 'Bonus' },
  { value: 'otro',      label: 'Otro' },
];
const TYPE_LABEL: Record<string, string> = Object.fromEntries(PAYMENT_TYPES.map(t => [t.value, t.label]));

const fmtARS = (n: number) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n);
// paidAt es una fecha-calendario (se guarda como YYYY-MM-DDT00:00:00Z). Formatear
// esa parte directo evita el corrimiento de un dia por zona horaria (UTC-3 leeria
// la medianoche UTC como el dia anterior).
const fmtDate = (iso: string) => {
  if (!iso) return '—';
  const m = String(iso).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return `${m[3]}/${m[2]}/${m[1].slice(2)}`;
  return new Date(iso).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

interface Employee {
  id: string;
  name: string;
  role?: string;
  config: any;
  summary: { totalPaid: number; paymentCount: number; lastPayment: string | null };
}

interface Payment {
  id: string;
  employeeId: string;
  employeeName: string;
  amount: number;
  paymentType: string;
  paymentMethod?: string;
  period?: string;
  notes?: string;
  paidAt: string;
}

interface CommissionRule {
  id: string;
  employeeId: string;
  employeeName?: string;
  matchType: string;
  matchValue: string;
  ruleType: string;
  value: number;
  notes?: string;
}

interface Commission {
  id: string;
  employeeId: string;
  employeeName?: string;
  appointmentCode?: string;
  serviceName: string;
  serviceAmount: number;
  ruleType: string;
  ruleValue: number;
  commissionAmount: number;
  status: string;
  createdAt: string;
  workDate?: string | null;
  detalle?: { cliente?: string | null; vehiculo?: string | null; items?: string[] } | null;
}

const StaffPaymentsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { empId: routeEmpId } = useParams();
  // Vistas: pagos (principal) · reglas (ABM de reglas) · empleado (comisiones de un empleado).
  const view = location.pathname.includes('/reglas') ? 'reglas'
    : (routeEmpId ? 'empleado' : 'pagos');
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<{ name: string; code: string }[]>([]);
  const [accounts, setAccounts] = useState<{ id: string; name: string; kind?: string; isDefault?: number }[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterEmployee, setFilterEmployee] = useState('all');
  // Por defecto la tabla muestra lo del MES actual; el período se puede cambiar a "Todo".
  const monthStart = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; })();
  const [dateFrom, setDateFrom] = useState(monthStart);
  const [dateTo, setDateTo] = useState('');
  const [periodMode, setPeriodMode] = useState<'month' | 'all' | 'custom'>('month');

  // Modal pago
  const [showPayModal, setShowPayModal] = useState(false);
  const [payForm, setPayForm] = useState({ employeeId: '', amount: '', paymentType: 'sueldo', paymentMethod: 'efectivo', accountId: '', period: '', notes: '', paidAt: hoyLocal() });
  const [saving, setSaving] = useState(false);
  // Comisiones pendientes del empleado elegido (para pre-cargar el monto cuando Tipo = Comisión)
  const [payPendingComms, setPayPendingComms] = useState<Commission[]>([]);

  // Comisiones
  const [commissions, setCommissions] = useState<Commission[]>([]);
  const [commissionRules, setCommissionRules] = useState<CommissionRule[]>([]);
  const [filterCommStatus, setFilterCommStatus] = useState('pending');
  const [onlyToday, setOnlyToday] = useState(true);
  const [commDateFrom, setCommDateFrom] = useState('');
  const [commDateTo, setCommDateTo] = useState('');
  const [selectedCommIds, setSelectedCommIds] = useState<string[]>([]);
  const [showRulePage, setShowRulePage] = useState(false);
  const [ruleForm, setRuleForm] = useState({ employeeId: '', matchType: 'category', matchValue: '', ruleType: 'percentage', value: '', notes: '' });
  const [savingRule, setSavingRule] = useState(false);
  const [payingComm, setPayingComm] = useState(false);
  // Modal de pago de comisiones seleccionadas (permite sumar plata extra antes de confirmar)
  const [showCommPayModal, setShowCommPayModal] = useState(false);
  const [commPay, setCommPay] = useState({ employeeId: '', employeeName: '', ids: [] as string[], base: 0, amount: '', method: 'efectivo', accountId: '', notes: '', paidAt: hoyLocal() });
  const methodLabel = useMemo(() => Object.fromEntries(paymentMethods.map(m => [m.code, m.name])) as Record<string, string>, [paymentMethods]);
  // Al personal se le paga en efectivo o por transferencia; tarjetas, cheques y cta cte no aplican.
  const staffMethods = useMemo(() => paymentMethods.filter(m => m.code === 'efectivo' || m.code === 'transferencia'), [paymentMethods]);
  // Fecha local de hoy (YYYY-MM-DD) para el filtro "solo hoy"
  const todayLocal = useMemo(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; }, []);
  // Comisiones a mostrar: por defecto solo las de hoy; al apagar el filtro, todas (incluye anteriores)
  const commissionsView = useMemo(() => {
    // Prioridad: rango de fechas (por fecha de trabajo). Si no hay rango, "solo hoy" o todo.
    if (commDateFrom || commDateTo) {
      return commissions.filter(c => {
        const d = c.workDate || c.createdAt?.slice(0, 10) || '';
        if (commDateFrom && d < commDateFrom) return false;
        if (commDateTo && d > commDateTo) return false;
        return true;
      });
    }
    return onlyToday ? commissions.filter(c => (c.workDate || c.createdAt?.slice(0, 10)) === todayLocal) : commissions;
  }, [commissions, onlyToday, todayLocal, commDateFrom, commDateTo]);
  const currentEmp = useMemo(() => employees.find(e => e.id === routeEmpId) || null, [employees, routeEmpId]);
  // Comisiones del empleado abierto (respeta el filtro "solo hoy")
  const empCommsView = useMemo(() => commissionsView.filter(c => c.employeeId === routeEmpId), [commissionsView, routeEmpId]);
  // Pagos del empleado abierto: se traen SIN limite de fechas (la lista de pagos
  // general esta acotada al mes) y se filtran con los mismos controles de arriba.
  const [empPaymentsAll, setEmpPaymentsAll] = useState<Payment[]>([]);
  const empPaymentsView = useMemo(() => {
    const list = empPaymentsAll.filter(p => p.employeeId === routeEmpId);
    if (commDateFrom || commDateTo) {
      return list.filter(p => {
        const d = (p.paidAt || '').slice(0, 10);
        if (commDateFrom && d < commDateFrom) return false;
        if (commDateTo && d > commDateTo) return false;
        return true;
      });
    }
    return onlyToday ? list.filter(p => (p.paidAt || '').slice(0, 10) === todayLocal) : list;
  }, [empPaymentsAll, routeEmpId, commDateFrom, commDateTo, onlyToday, todayLocal]);
  const empPaidTotal = useMemo(() => empPaymentsView.reduce((s, p) => s + p.amount, 0), [empPaymentsView]);
  // Cuentas coherentes con la forma de pago: efectivo → solo cajas; resto → bancos/billeteras
  const accountsFor = useCallback((method: string) => {
    const list = method === 'efectivo' ? accounts.filter(a => a.kind === 'efectivo') : accounts.filter(a => a.kind !== 'efectivo');
    return list.length ? list : accounts;
  }, [accounts]);
  const defaultAccountFor = useCallback((method: string) => {
    const list = accountsFor(method);
    return list.find(a => a.isDefault)?.id || list[0]?.id || '';
  }, [accountsFor]);
  const defaultAccountId = useMemo(() => defaultAccountFor('efectivo'), [defaultAccountFor]);
  const [serviceSearch, setServiceSearch] = useState('');
  const [serviceSearchResults, setServiceSearchResults] = useState<any[]>([]);
  const [serviceSearching, setServiceSearching] = useState(false);
  // Para múltiples servicios seleccionados
  const [selectedServices, setSelectedServices] = useState<string[]>([]);

  // Modal config empleado
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configEmployee, setConfigEmployee] = useState<Employee | null>(null);
  const [configForm, setConfigForm] = useState({ baseAmount: '', frequency: 'monthly', customDays: '30', notes: '' });
  const [savingConfig, setSavingConfig] = useState(false);

  // Expandir empleado para ver historial
  const [expanded, setExpanded] = useState<string | null>(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [empRes, payRes, commRes, rulesRes, methRes, acctRes] = await Promise.all([
        axios.get('/api/staff-payments/employees'),
        axios.get('/api/staff-payments', { params: { dateFrom, dateTo, employeeId: filterEmployee !== 'all' ? filterEmployee : undefined } }),
        axios.get('/api/staff-commissions', { params: { status: filterCommStatus !== 'all' ? filterCommStatus : undefined, dateFrom, dateTo } }),
        axios.get('/api/staff-commissions/rules'),
        axios.get('/api/settings/payment-methods').catch(() => ({ data: [] })),
        axios.get('/api/financial-accounts').catch(() => ({ data: { accounts: [] } })),
      ]);
      setEmployees(empRes.data.employees || []);
      setPayments(payRes.data.payments || []);
      setCommissions(commRes.data.commissions || []);
      setCommissionRules(rulesRes.data.rules || []);
      setPaymentMethods(Array.isArray(methRes.data) ? methRes.data : []);
      setAccounts(acctRes.data.accounts || []);
    } catch {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, [filterEmployee, dateFrom, dateTo, filterCommStatus]);

  // Historial completo de pagos del empleado abierto (se re-pide cuando cambia
  // `payments`, o sea despues de registrar/eliminar un pago)
  useEffect(() => {
    if (view !== 'empleado' || !routeEmpId) { setEmpPaymentsAll([]); return; }
    let cancelled = false;
    axios.get('/api/staff-payments', { params: { employeeId: routeEmpId } })
      .then(res => { if (!cancelled) setEmpPaymentsAll(res.data.payments || []); })
      .catch(() => { if (!cancelled) setEmpPaymentsAll([]); });
    return () => { cancelled = true; };
  }, [view, routeEmpId, payments]);

  // Búsqueda de servicios para la regla
  useEffect(() => {
    if (ruleForm.matchType !== 'service_name' || serviceSearch.trim().length < 2) {
      setServiceSearchResults([]); return;
    }
    const timer = setTimeout(() => {
      setServiceSearching(true);
      axios.get(`/api/products?search=${encodeURIComponent(serviceSearch)}&limit=15`)
        .then(res => setServiceSearchResults(res.data?.products || []))
        .catch(() => setServiceSearchResults([]))
        .finally(() => setServiceSearching(false));
    }, 250);
    return () => clearTimeout(timer);
  }, [serviceSearch, ruleForm.matchType]);

  // Al abrir el pago con Tipo = Comisión + empleado, traer sus comisiones pendientes y pre-cargar el monto.
  useEffect(() => {
    if (!showPayModal || payForm.paymentType !== 'comision' || !payForm.employeeId) {
      setPayPendingComms([]);
      return;
    }
    let cancelled = false;
    axios.get('/api/staff-commissions', { params: { employeeId: payForm.employeeId, status: 'pending' } })
      .then(res => {
        if (cancelled) return;
        const list: Commission[] = res.data.commissions || [];
        setPayPendingComms(list);
        const total = list.reduce((s, c) => s + c.commissionAmount, 0);
        if (total > 0) {
          setPayForm(f => ({ ...f, amount: String(total), period: f.period || 'Comisiones pendientes' }));
        }
      })
      .catch(() => { if (!cancelled) setPayPendingComms([]); });
    return () => { cancelled = true; };
  }, [showPayModal, payForm.paymentType, payForm.employeeId]);

  const filteredEmployees = useMemo(() =>
    employees.filter(e => !search || e.name.toLowerCase().includes(search.toLowerCase())),
    [employees, search]
  );

  const paymentsByEmployee = useMemo(() => {
    const map: Record<string, Payment[]> = {};
    payments.forEach(p => {
      if (!map[p.employeeId]) map[p.employeeId] = [];
      map[p.employeeId].push(p);
    });
    return map;
  }, [payments]);

  // Total general
  const totalGeneral = useMemo(() => payments.reduce((s, p) => s + p.amount, 0), [payments]);

  const openPayModal = (emp?: Employee) => {
    setPayForm({ employeeId: emp?.id || '', amount: '', paymentType: 'sueldo', paymentMethod: 'efectivo', accountId: defaultAccountId, period: '', notes: '', paidAt: hoyLocal() });
    setShowPayModal(true);
  };

  const handlePay = async () => {
    if (!payForm.employeeId) { toast.error('Seleccioná un empleado'); return; }
    if (!payForm.amount || Number(payForm.amount) <= 0) { toast.error('Ingresá un monto válido'); return; }
    setSaving(true);
    try {
      // Si es Comisión y el empleado tiene pendientes, pagarlas vía /pay (crea el pago y las marca pagadas).
      if (payForm.paymentType === 'comision' && payPendingComms.length > 0) {
        const res = await axios.post('/api/staff-commissions/pay', {
          employeeId: payForm.employeeId,
          commissionIds: payPendingComms.map(c => c.id),
          amount: Number(payForm.amount) > 0 ? Number(payForm.amount) : undefined,
          paymentMethod: payForm.paymentMethod || undefined,
          accountId: payForm.accountId || undefined,
          notes: payForm.notes || payForm.period || undefined,
          paidAt: new Date(payForm.paidAt).toISOString(),
        });
        toast.success(`Comisiones pagadas: ${res.data.paidCount} — ${fmtARS(res.data.totalAmount)}`);
      } else {
        await axios.post('/api/staff-payments', { ...payForm, amount: Number(payForm.amount), paidAt: new Date(payForm.paidAt).toISOString() });
        toast.success('Pago registrado');
      }
      setShowPayModal(false);
      fetchAll();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al registrar');
    } finally { setSaving(false); }
  };

  const openConfigModal = (emp: Employee) => {
    setConfigEmployee(emp);
    setConfigForm({
      baseAmount: emp.config?.baseAmount?.toString() || '',
      frequency: emp.config?.frequency || 'monthly',
      customDays: emp.config?.customDays?.toString() || '30',
      notes: emp.config?.notes || ''
    });
    setShowConfigModal(true);
  };

  const handleSaveConfig = async () => {
    if (!configEmployee) return;
    setSavingConfig(true);
    try {
      await axios.put(`/api/staff-payments/config/${configEmployee.id}`, {
        baseAmount: Number(configForm.baseAmount) || 0,
        frequency: configForm.frequency,
        customDays: Number(configForm.customDays) || 30,
        notes: configForm.notes
      });
      toast.success('Configuración guardada');
      setShowConfigModal(false);
      fetchAll();
    } catch { toast.error('Error al guardar'); }
    finally { setSavingConfig(false); }
  };

  const handleDeletePayment = async (p: Payment) => {
    if (!(await confirmDialog({ title: 'Eliminar pago', description: `¿Eliminar pago de ${fmtARS(p.amount)} a ${p.employeeName}?`, confirmText: 'Eliminar' }))) return;
    try {
      await axios.delete(`/api/staff-payments/${p.id}`);
      toast.success('Pago eliminado');
      fetchAll();
    } catch { toast.error('Error al eliminar'); }
  };

  // ── Comisiones ──────────────────────────────────────────────
  const handleSaveRule = async () => {
    const matchValue = ruleForm.matchType === 'service_name'
      ? (selectedServices.length > 0 ? JSON.stringify(selectedServices) : '')
      : ruleForm.matchValue;
    if (!ruleForm.employeeId || !matchValue || !ruleForm.value) {
      toast.error('Completá todos los campos obligatorios'); return;
    }
    setSavingRule(true);
    try {
      await axios.post('/api/staff-commissions/rules', { ...ruleForm, matchValue, value: Number(ruleForm.value) });
      toast.success('Regla guardada');
      setShowRulePage(false);
      fetchAll();
    } catch (err: any) { toast.error(err.response?.data?.error || 'Error'); }
    finally { setSavingRule(false); }
  };

  const handleDeleteRule = async (id: string) => {
    if (!(await confirmDialog({ title: 'Eliminar regla', description: '¿Eliminar esta regla de comisión?', confirmText: 'Eliminar' }))) return;
    try {
      await axios.delete(`/api/staff-commissions/rules/${id}`);
      toast.success('Regla eliminada');
      fetchAll();
    } catch { toast.error('Error'); }
  };

  const handleDeleteCommission = async (id: string) => {
    if (!(await confirmDialog({ title: 'Eliminar comisión', description: '¿Eliminar esta comisión pendiente?', confirmText: 'Eliminar' }))) return;
    try {
      await axios.delete(`/api/staff-commissions/${id}`);
      toast.success('Comisión eliminada');
      fetchAll();
    } catch { toast.error('Error'); }
  };

  // Abre el modal de confirmación (no paga de una) para poder ajustar el total / sumar extra
  const openCommPayModal = (employeeId: string, employeeName: string) => {
    const ids = selectedCommIds.filter(id => commissions.find(c => c.id === id && c.employeeId === employeeId));
    if (!ids.length) { toast.error('Seleccioná al menos una comisión'); return; }
    const base = commissions.filter(c => ids.includes(c.id)).reduce((s, c) => s + c.commissionAmount, 0);
    setCommPay({ employeeId, employeeName, ids, base, amount: String(base), method: 'efectivo', accountId: defaultAccountId, notes: '', paidAt: hoyLocal() });
    setShowCommPayModal(true);
  };

  const handleConfirmCommPay = async () => {
    const amount = Number(commPay.amount);
    if (!(amount > 0)) { toast.error('Ingresá un monto válido'); return; }
    setPayingComm(true);
    try {
      const res = await axios.post('/api/staff-commissions/pay', {
        employeeId: commPay.employeeId,
        commissionIds: commPay.ids,
        amount,
        paymentMethod: commPay.method || undefined,
        accountId: commPay.accountId || undefined,
        notes: commPay.notes || undefined,
        paidAt: new Date(commPay.paidAt).toISOString(),
      });
      toast.success(`Pagadas ${res.data.paidCount} comisiones — ${fmtARS(res.data.totalAmount)}`);
      setSelectedCommIds([]);
      setShowCommPayModal(false);
      fetchAll();
    } catch (err: any) { toast.error(err.response?.data?.error || 'Error'); }
    finally { setPayingComm(false); }
  };

  const pendingByEmployee = useMemo(() => {
    const map: Record<string, Commission[]> = {};
    commissions.filter(c => c.status === 'pending').forEach(c => {
      if (!map[c.employeeId]) map[c.employeeId] = [];
      map[c.employeeId].push(c);
    });
    return map;
  }, [commissions]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:justify-between md:items-start">
        <div>
          {view !== 'pagos' && (
            <button type="button" onClick={() => navigate('/staff-payments')} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-1">
              <ChevronDownIcon className="h-3.5 w-3.5 rotate-90" /> Volver a Pagos al personal
            </button>
          )}
          <h2 className="text-2xl font-bold tracking-tight">
            {view === 'reglas' ? 'Reglas de comisión'
              : view === 'empleado' ? `Comisiones — ${currentEmp?.name || 'Empleado'}`
              : 'Pagos al personal'}
          </h2>
        </div>
        <div className="flex gap-2">
          {view === 'reglas' && (
            <Button variant="outline" size="sm" className="h-8 rounded-lg gap-1.5 px-3" onClick={() => { setRuleForm({ employeeId: '', matchType: 'category', matchValue: '', ruleType: 'percentage', value: '', notes: '' }); setServiceSearch(''); setServiceSearchResults([]); setSelectedServices([]); setShowRulePage(true); }}>
              <PlusIcon className="h-4 w-4" />
              Nueva regla comisión
            </Button>
          )}
          {view === 'pagos' && (<>
            <Button variant="outline" size="sm" className="h-8 rounded-lg gap-1.5 px-3" onClick={() => navigate('/staff-payments/reglas')}>
              Reglas de comisión
            </Button>
            <Button
              onClick={() => openPayModal()}
              size="sm"
              className="inline-flex items-center justify-center whitespace-nowrap text-sm font-bold h-8 rounded-lg gap-1.5 px-3 bg-black dark:bg-white text-white dark:text-black shadow-xs hover:bg-black/90 dark:hover:bg-white/90"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Registrar pago
            </Button>
          </>)}
          {view === 'empleado' && currentEmp && (
            <Button onClick={() => openPayModal(currentEmp)} size="sm" className="h-8 rounded-lg gap-1.5 px-3 bg-black dark:bg-white text-white dark:text-black hover:bg-black/90 dark:hover:bg-white/90">
              <PlusIcon className="h-4 w-4" /> Registrar pago
            </Button>
          )}
        </div>
      </div>

      {/* ── Vista PAGOS ── */}
      {view === 'pagos' && (<>


      {/* Resumen general */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Total pagado</p>
            <p className="text-xl font-bold mt-0.5">{fmtARS(totalGeneral)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Pagos registrados</p>
            <p className="text-xl font-bold mt-0.5">{payments.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Empleados</p>
            <p className="text-xl font-bold mt-0.5">{employees.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Promedio por pago</p>
            <p className="text-xl font-bold mt-0.5">{payments.length > 0 ? fmtARS(totalGeneral / payments.length) : '—'}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <div className="flex gap-3 flex-wrap items-end">
        <div className="relative">
          <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none z-10" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar empleado..." className="h-8 pl-8 w-[220px] rounded-lg" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Empleado</Label>
          <Select value={filterEmployee} onValueChange={setFilterEmployee}>
            <SelectTrigger className="h-8 rounded-lg w-[200px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Período</Label>
          <Select
            value={periodMode}
            onValueChange={(v: any) => {
              setPeriodMode(v);
              if (v === 'month') { setDateFrom(monthStart); setDateTo(''); }
              else if (v === 'all') { setDateFrom(''); setDateTo(''); }
            }}
          >
            <SelectTrigger className="h-8 rounded-lg w-[160px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="month">Mes actual</SelectItem>
              <SelectItem value="all">Todo el historial</SelectItem>
              <SelectItem value="custom">Personalizado</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Desde</Label>
          <DatePicker value={dateFrom} onChange={v => { setDateFrom(v); setPeriodMode('custom'); }} className="h-8 rounded-lg w-[140px]" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Hasta</Label>
          <DatePicker value={dateTo} onChange={v => { setDateTo(v); setPeriodMode('custom'); }} className="h-8 rounded-lg w-[140px]" />
        </div>
        {(periodMode !== 'month' || filterEmployee !== 'all') && (
          <Button variant="outline" size="sm" className="h-8 rounded-lg self-end" onClick={() => { setPeriodMode('month'); setDateFrom(monthStart); setDateTo(''); setFilterEmployee('all'); }}>
            Reset
          </Button>
        )}
      </div>

      {/* Lista de empleados con historial */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" /></div>
          ) : filteredEmployees.length === 0 ? (
            <div className="text-center py-12">
              <UserIcon className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No hay empleados</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-muted/50 border-b">
                  <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Empleado</TableHead>
                  <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Rol</TableHead>
                  <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Frecuencia</TableHead>
                  <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Pagos</TableHead>
                  <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Total</TableHead>
                  <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Último pago</TableHead>
                  <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEmployees.map(emp => {
                  const empPayments = paymentsByEmployee[emp.id] || [];
                  const isExpanded = expanded === emp.id;
                  const empTotal = empPayments.reduce((s, p) => s + p.amount, 0);
                  return (
                    <React.Fragment key={emp.id}>
                      <TableRow className="border-b hover:bg-muted/50 cursor-pointer" onClick={() => navigate(`/staff-payments/empleado/${emp.id}`)}>
                        <TableCell className="p-2 align-middle font-medium whitespace-nowrap">{emp.name}</TableCell>
                        <TableCell className="p-2 align-middle whitespace-nowrap">{emp.role ? <Badge variant="secondary" className="text-xs">{emp.role}</Badge> : <span className="text-muted-foreground">—</span>}</TableCell>
                        <TableCell className="p-2 align-middle whitespace-nowrap text-sm text-muted-foreground">
                          {emp.config
                            ? `${FREQ_LABEL[emp.config.frequency] || emp.config.frequency}${emp.config.frequency === 'custom' ? ` (cada ${emp.config.customDays}d)` : ''}${emp.config.baseAmount > 0 ? ` · ${fmtARS(emp.config.baseAmount)}` : ''}`
                            : '—'}
                        </TableCell>
                        <TableCell className="p-2 align-middle whitespace-nowrap text-sm">{empPayments.length}</TableCell>
                        <TableCell className="p-2 align-middle whitespace-nowrap text-sm font-medium">{fmtARS(empTotal)}</TableCell>
                        <TableCell className="p-2 align-middle whitespace-nowrap text-sm text-muted-foreground">
                          {empPayments.length ? fmtDate(empPayments.reduce((a, b) => (a.paidAt > b.paidAt ? a : b)).paidAt) : '—'}
                        </TableCell>
                        <TableCell className="p-2 align-middle text-right" onClick={e => e.stopPropagation()}>
                          <div className="flex items-center gap-1 justify-end">
                            <Tip label="Registrar pago">
                              <Button variant="outline" size="icon" className="size-8" onClick={() => openPayModal(emp)}>
                                <CurrencyDollarIcon className="h-4 w-4" />
                              </Button>
                            </Tip>
                            <Tip label="Configurar frecuencia">
                              <Button variant="outline" size="icon" className="size-8" onClick={() => openConfigModal(emp)}>
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                              </Button>
                            </Tip>
                            <Tip label={isExpanded ? 'Cerrar historial' : 'Ver historial de pagos'}>
                              <Button variant="outline" size="icon" className="size-8" onClick={() => setExpanded(isExpanded ? null : emp.id)}>
                                {isExpanded ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
                              </Button>
                            </Tip>
                          </div>
                        </TableCell>
                      </TableRow>

                      {isExpanded && (
                        <TableRow className="bg-muted/20 hover:bg-muted/20">
                          <TableCell colSpan={7} className="p-0">
                            <div className="px-4 py-3">
                              {empPayments.length === 0 ? (
                                <p className="text-sm text-muted-foreground py-2 text-center">Sin pagos en el período seleccionado</p>
                              ) : (
                                <Table>
                                  <TableHeader>
                                    <TableRow className="hover:bg-transparent border-b">
                                      <TableHead className="h-8 px-2 text-xs font-medium text-muted-foreground">Fecha</TableHead>
                                      <TableHead className="h-8 px-2 text-xs font-medium text-muted-foreground">Tipo</TableHead>
                                      <TableHead className="h-8 px-2 text-xs font-medium text-muted-foreground">Forma</TableHead>
                                      <TableHead className="h-8 px-2 text-xs font-medium text-muted-foreground">Período</TableHead>
                                      <TableHead className="h-8 px-2 text-xs font-medium text-muted-foreground">Notas</TableHead>
                                      <TableHead className="h-8 px-2 text-xs font-medium text-muted-foreground text-right">Monto</TableHead>
                                      <TableHead className="h-8 w-10"></TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {empPayments.map(p => (
                                      <TableRow key={p.id} className="border-b hover:bg-muted/40">
                                        <TableCell className="p-2 text-sm">{fmtDate(p.paidAt)}</TableCell>
                                        <TableCell className="p-2"><Badge variant="secondary" className="text-xs">{TYPE_LABEL[p.paymentType] || p.paymentType}</Badge></TableCell>
                                        <TableCell className="p-2 text-sm text-muted-foreground">{p.paymentMethod ? (methodLabel[p.paymentMethod] || p.paymentMethod) : '—'}</TableCell>
                                        <TableCell className="p-2 text-sm text-muted-foreground">{p.period || '—'}</TableCell>
                                        <TableCell className="p-2 text-sm text-muted-foreground max-w-[200px] truncate">{p.notes || '—'}</TableCell>
                                        <TableCell className="p-2 text-sm font-semibold text-right">{fmtARS(p.amount)}</TableCell>
                                        <TableCell className="p-2 text-right">
                                          <Button variant="ghost" size="icon" className="size-7 text-muted-foreground hover:text-red-500" onClick={() => handleDeletePayment(p)}>
                                            <TrashIcon className="h-3.5 w-3.5" />
                                          </Button>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                    <TableRow className="bg-muted/30 font-semibold">
                                      <TableCell colSpan={5} className="p-2 text-sm text-right">Total:</TableCell>
                                      <TableCell className="p-2 text-sm text-right">{fmtARS(empPayments.reduce((s, p) => s + p.amount, 0))}</TableCell>
                                      <TableCell />
                                    </TableRow>
                                  </TableBody>
                                </Table>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
            </div>
          )}
        </CardContent>
      </Card>
      </>)}

      {/* ── Vista REGLAS ── */}
      {view === 'reglas' && (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">Reglas que generan comisiones automáticamente al completar la OT o facturar el remito. Con "Nueva regla comisión" agregás una.</p>
        {commissionRules.length === 0 ? (
          <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">Todavía no hay reglas de comisión.</CardContent></Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="px-4 py-2 border-b border-border bg-muted/30">
                <p className="text-xs font-semibold uppercase text-muted-foreground">Reglas activas</p>
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent border-b">
                    <TableHead className="h-8 px-3 text-xs">Empleado</TableHead>
                    <TableHead className="h-8 px-3 text-xs">Aplica a</TableHead>
                    <TableHead className="h-8 px-3 text-xs">Comisión</TableHead>
                    <TableHead className="h-8 w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {commissionRules.map(r => (
                    <TableRow key={r.id} className="border-b hover:bg-muted/30">
                      <TableCell className="p-2 text-sm font-medium">{r.employeeName || r.employeeId}</TableCell>
                      <TableCell className="p-2 text-sm">
                        <Badge variant="outline" className="text-xs">
                          {r.matchType === 'category' ? 'Categoría' : 'Servicio'}: {(() => {
                            try {
                              const arr = JSON.parse(r.matchValue);
                              return Array.isArray(arr) ? arr.join(', ') : r.matchValue;
                            } catch { return r.matchValue; }
                          })()}
                        </Badge>
                      </TableCell>
                      <TableCell className="p-2 text-sm font-semibold">
                        {r.ruleType === 'percentage' ? `${r.value}%` : fmtARS(r.value)} {r.ruleType === 'fixed' ? 'fijo' : ''}
                      </TableCell>
                      <TableCell className="p-2 text-right">
                        <Button variant="ghost" size="icon" className="size-7 text-muted-foreground hover:text-red-500" onClick={() => handleDeleteRule(r.id)}>
                          <TrashIcon className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
      )}

      {/* ── Vista EMPLEADO (comisiones de un empleado) ── */}
      {view === 'empleado' && (
      <div className="space-y-4">
        <div className="flex items-center justify-end gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => { setOnlyToday(v => !v); setCommDateFrom(''); setCommDateTo(''); }}
            className={`h-8 rounded-lg px-3 text-xs font-medium border transition-colors ${(onlyToday && !commDateFrom && !commDateTo) ? 'bg-foreground text-background border-foreground' : 'bg-background text-muted-foreground border-border hover:text-foreground'}`}
          >
            {(onlyToday && !commDateFrom && !commDateTo) ? 'Solo hoy' : 'Todas las fechas'}
          </button>
          <div className="flex items-center gap-1">
            <Label className="text-xs text-muted-foreground">Desde</Label>
            <DatePicker value={commDateFrom} onChange={v => { setCommDateFrom(v); setOnlyToday(false); }} className="h-8 rounded-lg w-[140px]" />
          </div>
          <div className="flex items-center gap-1">
            <Label className="text-xs text-muted-foreground">Hasta</Label>
            <DatePicker value={commDateTo} onChange={v => { setCommDateTo(v); setOnlyToday(false); }} className="h-8 rounded-lg w-[140px]" />
          </div>
          {(commDateFrom || commDateTo) && (
            <Button variant="outline" size="sm" className="h-8 rounded-lg" onClick={() => { setCommDateFrom(''); setCommDateTo(''); }}>Limpiar</Button>
          )}
          <Select value={filterCommStatus} onValueChange={setFilterCommStatus}>
            <SelectTrigger className="h-8 rounded-lg w-[140px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pendientes</SelectItem>
              <SelectItem value="paid">Pagadas</SelectItem>
              <SelectItem value="all">Todas</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Comisiones generadas */}
        {empCommsView.length === 0 ? (
          <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">
            {onlyToday
              ? 'No hay comisiones de hoy para este empleado. Tocá "Todas las fechas" para ver las anteriores.'
              : (filterCommStatus === 'pending' ? 'No hay comisiones pendientes' : 'No hay comisiones en este período')}
          </CardContent></Card>
        ) : (
          Object.entries(
            empCommsView.reduce((acc: Record<string, Commission[]>, c) => {
              if (!acc[c.employeeId]) acc[c.employeeId] = [];
              acc[c.employeeId].push(c);
              return acc;
            }, {})
          ).map(([empId, emComms]) => {
            const empName = emComms[0].employeeName || empId;
            const pendingComms = emComms.filter(c => c.status === 'pending');
            const pendingTotal = pendingComms.reduce((s, c) => s + c.commissionAmount, 0);
            const empSelectedIds = selectedCommIds.filter(id => emComms.some(c => c.id === id));
            return (
              <Card key={empId}>
                <CardContent className="p-0">
                  <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-muted/20">
                    <div className="flex items-center gap-3">
                      <span className="font-semibold text-sm">{empName}</span>
                      {pendingComms.length > 0 && (
                        <Badge variant="secondary" className="text-xs">{pendingComms.length} pendiente{pendingComms.length > 1 ? 's' : ''} · {fmtARS(pendingTotal)}</Badge>
                      )}
                    </div>
                    {pendingComms.length > 0 && (
                      <Button
                        size="sm"
                        className="h-7 px-3 text-xs gap-1"
                        disabled={payingComm || empSelectedIds.length === 0}
                        onClick={() => openCommPayModal(empId, empName)}
                      >
                        <CheckIcon className="h-3.5 w-3.5" />
                        Pagar seleccionadas ({empSelectedIds.length > 0 ? fmtARS(emComms.filter(c => empSelectedIds.includes(c.id)).reduce((s, c) => s + c.commissionAmount, 0)) : '0'})
                      </Button>
                    )}
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow className="hover:bg-transparent border-b">
                        {filterCommStatus !== 'paid' && (
                          <TableHead className="h-8 w-10 px-3">
                            {pendingComms.length > 0 && (
                              <input
                                type="checkbox"
                                aria-label="Marcar todas"
                                checked={pendingComms.every(c => selectedCommIds.includes(c.id))}
                                ref={el => { if (el) el.indeterminate = pendingComms.some(c => selectedCommIds.includes(c.id)) && !pendingComms.every(c => selectedCommIds.includes(c.id)); }}
                                onChange={() => {
                                  const ids = pendingComms.map(c => c.id);
                                  const allSel = ids.every(id => selectedCommIds.includes(id));
                                  setSelectedCommIds(prev => allSel ? prev.filter(id => !ids.includes(id)) : prev.concat(ids.filter(id => !prev.includes(id))));
                                }}
                              />
                            )}
                          </TableHead>
                        )}
                        <TableHead className="h-8 px-3 text-xs">OT / Remito</TableHead>
                        <TableHead className="h-8 px-3 text-xs">Servicio</TableHead>
                        <TableHead className="h-8 px-3 text-xs text-right">Base</TableHead>
                        <TableHead className="h-8 px-3 text-xs text-right">Comisión</TableHead>
                        <TableHead className="h-8 px-3 text-xs">Estado</TableHead>
                        <TableHead className="h-8 w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {emComms.map(c => (
                        <TableRow key={c.id} className="border-b hover:bg-muted/30">
                          {filterCommStatus !== 'paid' && (
                            <TableCell className="px-3 py-2">
                              {c.status === 'pending' && (
                                <input type="checkbox" checked={selectedCommIds.includes(c.id)}
                                  onChange={() => setSelectedCommIds(prev => prev.includes(c.id) ? prev.filter(x => x !== c.id) : [...prev, c.id])}
                                />
                              )}
                            </TableCell>
                          )}
                          <TableCell className="px-3 py-2 text-sm font-mono">
                            {c.appointmentCode ? (
                              c.detalle ? (
                                <Tip side="right" label={
                                  <div className="max-w-[280px] space-y-1 text-left">
                                    {c.detalle.cliente ? <p className="font-semibold">{c.detalle.cliente}</p> : null}
                                    {c.detalle.vehiculo ? (
                                      <p className="flex items-center gap-1 text-xs font-medium">
                                        <TruckIcon className="h-3.5 w-3.5 shrink-0" />
                                        {c.detalle.vehiculo}
                                      </p>
                                    ) : null}
                                    {(c.detalle.items || []).length > 0 ? (
                                      <ul className="text-xs list-disc pl-4 space-y-0.5">
                                        {(c.detalle.items || []).slice(0, 8).map((it, i) => <li key={i}>{it}</li>)}
                                        {(c.detalle.items || []).length > 8 ? <li>… y {(c.detalle.items || []).length - 8} más</li> : null}
                                      </ul>
                                    ) : <p className="text-xs opacity-70">Sin detalle de items</p>}
                                  </div>
                                }>
                                  <span className="underline decoration-dotted underline-offset-4 cursor-help">{c.appointmentCode}</span>
                                </Tip>
                              ) : c.appointmentCode
                            ) : '—'}
                          </TableCell>
                          <TableCell className="px-3 py-2 text-sm">{c.serviceName}</TableCell>
                          <TableCell className="px-3 py-2 text-sm text-right text-muted-foreground">{fmtARS(c.serviceAmount)}</TableCell>
                          <TableCell className="px-3 py-2 text-sm text-right font-semibold">
                            {fmtARS(c.commissionAmount)}
                            <span className="text-xs text-muted-foreground ml-1">
                              ({c.ruleType === 'percentage' ? `${c.ruleValue}%` : 'fijo'})
                            </span>
                          </TableCell>
                          <TableCell className="px-3 py-2">
                            <Badge variant={c.status === 'paid' ? 'secondary' : 'outline'} className="text-xs">
                              {c.status === 'paid' ? 'Pagada' : 'Pendiente'}
                            </Badge>
                          </TableCell>
                          <TableCell className="px-2 py-2 text-right">
                            {c.status === 'pending' && (
                              <Button variant="ghost" size="icon" className="size-7 text-muted-foreground hover:text-red-500" onClick={() => handleDeleteCommission(c.id)}>
                                <TrashIcon className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            );
          })
        )}

        {/* Pagos que se le hicieron al empleado (mismo filtro de fechas de arriba) */}
        <Card>
          <CardContent className="p-0">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-muted/20">
              <span className="font-semibold text-sm">Pagos a {currentEmp?.name || 'este empleado'}</span>
              <span className="text-sm text-muted-foreground">
                {empPaymentsView.length} pago{empPaymentsView.length === 1 ? '' : 's'} · <span className="font-semibold text-foreground">{fmtARS(empPaidTotal)}</span>
              </span>
            </div>
            {empPaymentsView.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                {(onlyToday && !commDateFrom && !commDateTo)
                  ? 'No se le pagó nada hoy. Tocá "Todas las fechas" para ver el historial.'
                  : 'Sin pagos en este período'}
              </p>
            ) : (
              <Table className="cq-table">
                <TableHeader>
                  <TableRow className="hover:bg-transparent border-b">
                    <TableHead className="h-8 px-3 text-xs">Fecha</TableHead>
                    <TableHead className="h-8 px-3 text-xs">Tipo</TableHead>
                    <TableHead className="h-8 px-3 text-xs cq-hide-narrow">Forma</TableHead>
                    <TableHead className="h-8 px-3 text-xs cq-hide-narrow">Período</TableHead>
                    <TableHead className="h-8 px-3 text-xs cq-hide-narrow">Notas</TableHead>
                    <TableHead className="h-8 px-3 text-xs text-right">Monto</TableHead>
                    <TableHead className="h-8 w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {empPaymentsView.map(p => (
                    <TableRow key={p.id} className="border-b hover:bg-muted/30">
                      <TableCell className="px-3 py-2 text-sm whitespace-nowrap">{fmtDate(p.paidAt)}</TableCell>
                      <TableCell className="px-3 py-2">
                        <Badge variant="secondary" className="text-xs">{TYPE_LABEL[p.paymentType] || p.paymentType}</Badge>
                      </TableCell>
                      <TableCell className="px-3 py-2 text-sm text-muted-foreground cq-hide-narrow">{p.paymentMethod ? (methodLabel[p.paymentMethod] || p.paymentMethod) : '—'}</TableCell>
                      <TableCell className="px-3 py-2 text-sm text-muted-foreground cq-hide-narrow">{p.period || '—'}</TableCell>
                      <TableCell className="px-3 py-2 text-sm text-muted-foreground cq-hide-narrow cq-clamp-narrow truncate">{p.notes || '—'}</TableCell>
                      <TableCell className="px-3 py-2 text-sm font-semibold text-right whitespace-nowrap">{fmtARS(p.amount)}</TableCell>
                      <TableCell className="px-2 py-2 text-right">
                        <Tip label="Eliminar pago">
                          <Button variant="ghost" size="icon" className="size-7 text-muted-foreground hover:text-red-500" onClick={() => handleDeletePayment(p)}>
                            <TrashIcon className="h-3.5 w-3.5" />
                          </Button>
                        </Tip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
      )}

      {/* Modal pagar comisiones seleccionadas (con ajuste / plata extra) */}
      <Dialog open={showCommPayModal} onOpenChange={o => { if (!o) setShowCommPayModal(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Pagar comisiones — {commPay.employeeName}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-1">
            <div className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm flex items-center justify-between">
              <span className="text-muted-foreground">{commPay.ids.length} comisión{commPay.ids.length > 1 ? 'es' : ''} seleccionada{commPay.ids.length > 1 ? 's' : ''}</span>
              <span className="font-semibold">{fmtARS(commPay.base)}</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Total a pagar *</Label>
                <Input type="number" min="0" value={commPay.amount} onChange={e => setCommPay(f => ({ ...f, amount: e.target.value }))} placeholder="0" />
              </div>
              <div className="space-y-1.5">
                <Label>Fecha</Label>
                <DatePicker value={commPay.paidAt} onChange={v => setCommPay(f => ({ ...f, paidAt: v }))} />
              </div>
            </div>
            {(() => {
              const diff = Math.round((Number(commPay.amount || 0) - commPay.base) * 100) / 100;
              if (!diff) return null;
              return (
                <p className={`text-xs -mt-2 ${diff > 0 ? 'text-emerald-600' : 'text-amber-600'}`}>
                  {diff > 0 ? `Plata extra: +${fmtARS(diff)}` : `Ajuste: ${fmtARS(diff)}`} sobre la comisión ({fmtARS(commPay.base)}).
                </p>
              );
            })()}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Forma de pago</Label>
                <Select value={commPay.method} onValueChange={v => setCommPay(f => ({ ...f, method: v, accountId: defaultAccountFor(v) }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>{staffMethods.map(m => <SelectItem key={m.code} value={m.code}>{m.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Cuenta (de dónde sale)</Label>
                <Select value={commPay.accountId} onValueChange={v => setCommPay(f => ({ ...f, accountId: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar cuenta..." /></SelectTrigger>
                  <SelectContent>{accountsFor(commPay.method).map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Notas (opcional)</Label>
              <Input value={commPay.notes} onChange={e => setCommPay(f => ({ ...f, notes: e.target.value }))} placeholder="Ej: transferencia Galicia / de dónde salió" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCommPayModal(false)}>Cancelar</Button>
            <Button onClick={handleConfirmCommPay} disabled={payingComm}>{payingComm ? 'Pagando...' : `Pagar ${fmtARS(Number(commPay.amount || 0))}`}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal registrar pago */}
      <Dialog open={showPayModal} onOpenChange={o => { if (!o) setShowPayModal(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Registrar pago</DialogTitle></DialogHeader>
          <div className="space-y-4 py-1">
            <div className="space-y-1.5">
              <Label>Empleado *</Label>
              <Select value={payForm.employeeId} onValueChange={v => setPayForm(f => ({ ...f, employeeId: v }))}>
                <SelectTrigger><SelectValue placeholder="Seleccionar empleado..." /></SelectTrigger>
                <SelectContent>
                  {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Monto *</Label>
                <Input type="number" min="0" value={payForm.amount} onChange={e => setPayForm(f => ({ ...f, amount: e.target.value }))} placeholder="0" />
              </div>
              <div className="space-y-1.5">
                <Label>Fecha</Label>
                <DatePicker value={payForm.paidAt} onChange={v => setPayForm(f => ({ ...f, paidAt: v }))} />
              </div>
            </div>
            {payForm.paymentType === 'comision' && payForm.employeeId && (
              payPendingComms.length > 0 ? (
                <p className="text-xs text-emerald-600 -mt-2">
                  {payPendingComms.length} comisión{payPendingComms.length > 1 ? 'es' : ''} pendiente{payPendingComms.length > 1 ? 's' : ''} · se marcarán como pagadas al registrar.
                </p>
              ) : (
                <p className="text-xs text-muted-foreground -mt-2">Sin comisiones pendientes para este empleado.</p>
              )
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Tipo</Label>
                <Select value={payForm.paymentType} onValueChange={v => setPayForm(f => ({ ...f, paymentType: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{PAYMENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Forma de pago</Label>
                <Select value={payForm.paymentMethod} onValueChange={v => setPayForm(f => ({ ...f, paymentMethod: v, accountId: defaultAccountFor(v) }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>{staffMethods.map(m => <SelectItem key={m.code} value={m.code}>{m.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Cuenta (de dónde sale)</Label>
                <Select value={payForm.accountId} onValueChange={v => setPayForm(f => ({ ...f, accountId: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar cuenta..." /></SelectTrigger>
                  <SelectContent>{accountsFor(payForm.paymentMethod).map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Período (opcional)</Label>
                <Input value={payForm.period} onChange={e => setPayForm(f => ({ ...f, period: e.target.value }))} placeholder="Ej: Junio 2026" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Notas (opcional)</Label>
              <Input value={payForm.notes} onChange={e => setPayForm(f => ({ ...f, notes: e.target.value }))} placeholder="Observaciones..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayModal(false)}>Cancelar</Button>
            <Button onClick={handlePay} disabled={saving}>{saving ? 'Guardando...' : 'Registrar pago'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal configurar empleado */}
      <Dialog open={showConfigModal} onOpenChange={o => { if (!o) setShowConfigModal(false); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Configurar — {configEmployee?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-1">
            <div className="space-y-1.5">
              <Label>Monto base</Label>
              <Input type="number" min="0" value={configForm.baseAmount} onChange={e => setConfigForm(f => ({ ...f, baseAmount: e.target.value }))} placeholder="0" />
            </div>
            <div className="space-y-1.5">
              <Label>Frecuencia de pago</Label>
              <Select value={configForm.frequency} onValueChange={v => setConfigForm(f => ({ ...f, frequency: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{FREQUENCIES.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {configForm.frequency === 'custom' && (
              <div className="space-y-1.5">
                <Label>Cada cuántos días</Label>
                <Input type="number" min="1" value={configForm.customDays} onChange={e => setConfigForm(f => ({ ...f, customDays: e.target.value }))} />
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Notas (opcional)</Label>
              <Input value={configForm.notes} onChange={e => setConfigForm(f => ({ ...f, notes: e.target.value }))} placeholder="Ej: turno fijo lunes-viernes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigModal(false)}>Cancelar</Button>
            <Button onClick={handleSaveConfig} disabled={savingConfig}>{savingConfig ? 'Guardando...' : 'Guardar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal nueva regla de comisión */}
      {/* Página completa: nueva regla de comisión */}
      <Dialog open={showRulePage} onOpenChange={o => { if (!o) setShowRulePage(false); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nueva regla de comisión</DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-1">

            {/* Empleado */}
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Empleado</Label>
              <Select value={ruleForm.employeeId} onValueChange={v => setRuleForm(f => ({ ...f, employeeId: v }))}>
                <SelectTrigger><SelectValue placeholder="Seleccionar empleado..." /></SelectTrigger>
                <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}{e.role ? ` · ${e.role}` : ''}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            {/* Aplica a */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Aplica a</Label>
              {/* Toggle segmented */}
              <div className="flex rounded-lg border border-border overflow-hidden">
                <button type="button"
                  onClick={() => setRuleForm(f => ({ ...f, matchType: 'category', matchValue: '' }))}
                  className={`flex-1 py-2 text-sm font-medium transition-colors ${ruleForm.matchType === 'category' ? 'bg-foreground text-background' : 'bg-background text-muted-foreground hover:text-foreground'}`}>
                  Por área
                </button>
                <button type="button"
                  onClick={() => { setRuleForm(f => ({ ...f, matchType: 'service_name', matchValue: '' })); setSelectedServices([]); }}
                  className={`flex-1 py-2 text-sm font-medium transition-colors border-l border-border ${ruleForm.matchType === 'service_name' ? 'bg-foreground text-background' : 'bg-background text-muted-foreground hover:text-foreground'}`}>
                  Servicios específicos
                </button>
              </div>

              {ruleForm.matchType === 'category' ? (
                <Select value={ruleForm.matchValue} onValueChange={v => setRuleForm(f => ({ ...f, matchValue: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar área..." /></SelectTrigger>
                  <SelectContent>
                    {[['detailing','Detailing'],['lavadero','Lavadero'],['gomeria','Gomería'],['mecanica','Mecánica'],['lubricentro','Lubricentro'],['alineacion','Alineación'],['balanceo','Balanceo'],['electricidad','Electricidad']].map(([v,l]) => (
                      <SelectItem key={v} value={v}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="space-y-2">
                  {selectedServices.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {selectedServices.map(svc => (
                        <span key={svc} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-muted border border-border">
                          {svc}
                          <button type="button" onClick={() => setSelectedServices(p => p.filter(x => x !== svc))} className="ml-0.5 text-muted-foreground hover:text-red-500"><XMarkIcon className="h-3 w-3" /></button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
                    <Input value={serviceSearch} onChange={e => setServiceSearch(e.target.value)} placeholder="Buscar servicio..." className="pl-9" />
                    {serviceSearchResults.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 rounded-md border border-border bg-popover shadow-lg max-h-[160px] overflow-y-auto">
                        {serviceSearchResults.filter(s => !selectedServices.includes(s.name)).map((s: any) => (
                          <button key={s.id} type="button" onMouseDown={e => e.preventDefault()}
                            onClick={() => { setSelectedServices(p => [...p, s.name]); setServiceSearch(''); setServiceSearchResults([]); }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-accent flex items-center justify-between gap-2">
                            <span>{s.name}</span>
                            <span className="text-xs text-emerald-600 shrink-0">+ Agregar</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Comisión */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Comisión</Label>
              <div className="flex rounded-lg border border-border overflow-hidden">
                <button type="button"
                  onClick={() => setRuleForm(f => ({ ...f, ruleType: 'percentage' }))}
                  className={`flex-1 py-2 text-sm font-medium transition-colors ${ruleForm.ruleType === 'percentage' ? 'bg-foreground text-background' : 'bg-background text-muted-foreground hover:text-foreground'}`}>
                  Porcentaje (%)
                </button>
                <button type="button"
                  onClick={() => setRuleForm(f => ({ ...f, ruleType: 'fixed' }))}
                  className={`flex-1 py-2 text-sm font-medium transition-colors border-l border-border ${ruleForm.ruleType === 'fixed' ? 'bg-foreground text-background' : 'bg-background text-muted-foreground hover:text-foreground'}`}>
                  Monto fijo ($)
                </button>
              </div>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Input type="number" min="0" value={ruleForm.value}
                    onChange={e => setRuleForm(f => ({ ...f, value: e.target.value }))}
                    placeholder={ruleForm.ruleType === 'percentage' ? '15' : '8000'}
                    className="w-32 pr-8"
                  />
                  <span className="absolute right-3 top-2 text-sm text-muted-foreground font-medium pointer-events-none">
                    {ruleForm.ruleType === 'percentage' ? '%' : '$'}
                  </span>
                </div>
                {ruleForm.value && Number(ruleForm.value) > 0 && (
                  <span className="text-sm text-emerald-600 font-medium">
                    {ruleForm.ruleType === 'percentage'
                      ? `Ej: servicio $10.000 → ${fmtARS(10000 * Number(ruleForm.value) / 100)}`
                      : `${fmtARS(Number(ruleForm.value))} por servicio`}
                  </span>
                )}
              </div>
            </div>

            {/* Notas */}
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Notas <span className="font-normal text-muted-foreground">(opcional)</span></Label>
              <Input value={ruleForm.notes} onChange={e => setRuleForm(f => ({ ...f, notes: e.target.value }))} placeholder="Ej: solo aplica los fines de semana" />
            </div>

          </div>

          <DialogFooter className="pt-2">
            <Button variant="outline" onClick={() => setShowRulePage(false)}>Cancelar</Button>
            <Button onClick={handleSaveRule} disabled={savingRule}>
              {savingRule ? 'Guardando...' : 'Guardar regla'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffPaymentsPage;
